'use client'

import React, { useState, useCallback } from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { NewsType } from '@/@types'
import { ITEMS_PER_PAGE } from '@/constants/AppConfig'
import ClientNewsCard from '@/components/ClientNewsCard'
import useInfiniteScroll from '@/hooks/useInfiniteScroll'
import { fetchConfig } from '@/utils/fetchConfig'

interface InfiniteScrollNewsLoaderProps {
  initialPosts: NewsType[]
  totalPages: number
  totalItems: number
}

interface ApiResponse {
  data: NewsType[]
  pagination: {
    totalPages: number
    totalItems: number
    currentPage: number
    total: number
    ipp: number
  }
}

export default function InfiniteScrollNewsLoader({
  initialPosts,
  totalPages,
  totalItems,
}: InfiniteScrollNewsLoaderProps) {
  const [posts, setPosts] = useState<NewsType[]>(initialPosts)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(totalPages > 1)
  const [announcement, setAnnouncement] = useState<string>('')

  const fetchMorePosts = useCallback(async () => {
    if (loading || !hasMore) return

    setLoading(true)
    setError(null)

    try {
      const nextPage = currentPage + 1
      const data: ApiResponse = await fetchConfig(
        `/articles?ipp=${ITEMS_PER_PAGE}&page=${nextPage}&orderBy=createdAt&order=desc`,
        'GET',
        0 // No caching for infinite scroll
      )

      if (data && data.data && data.data.length > 0) {
        // Filter out any duplicates based on articleId
        const newPosts = data.data.filter(
          (newPost) => !posts.some((existingPost) => existingPost.articleId === newPost.articleId)
        )

        if (newPosts.length > 0) {
          setPosts((prevPosts) => [...prevPosts, ...newPosts])
          setCurrentPage(nextPage)
          setAnnouncement(
            `Loaded ${newPosts.length} more articles. Total articles loaded: ${posts.length + newPosts.length}`
          )
          // Clear the announcement after screen readers have time to read it
          setTimeout(() => setAnnouncement(''), 1000)
        }

        setHasMore(nextPage < data.pagination.totalPages)
      } else {
        setHasMore(false)
      }
    } catch (err) {
      console.error('Error fetching more posts:', err)
      setError('Failed to load more articles. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [currentPage, hasMore, loading, posts])

  const observerRef = useInfiniteScroll(fetchMorePosts, hasMore)

  // Calculate the starting index for new posts (SSR posts are already rendered)
  const additionalPosts = posts.slice(initialPosts.length)

  return (
    <>
      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {announcement}
      </div>

      {/* Render additional posts loaded via infinite scroll */}
      {additionalPosts.map((post, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 5, xl: 4 }} key={post.articleId} sx={{ justifyContent: 'center' }}>
          <ClientNewsCard post={post} index={initialPosts.length + index} />
        </Grid>
      ))}

      {/* Loading indicator and infinite scroll trigger */}
      {hasMore && (
        <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', py: 4 }}>
          <div ref={observerRef} role="status" aria-live="polite">
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} aria-label="Loading more articles" />
                <Typography variant="body2" color="text.secondary">
                  Loading more articles...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Scroll down for more articles
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchMorePosts}
                  disabled={loading}
                  size="small"
                  aria-label="Load more articles manually"
                >
                  Load More Articles
                </Button>
              </Box>
            )}
          </div>
        </Grid>
      )}

      {/* Error handling */}
      {error && (
        <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Button variant="outlined" onClick={fetchMorePosts} disabled={loading} size="small" color="primary">
              Try Again
            </Button>
          </Box>
        </Grid>
      )}

      {/* End of content indicator */}
      {!hasMore && posts.length > initialPosts.length && (
        <Grid size={{ xs: 12 }} sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              py: 3,
              px: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              🎉 You&apos;ve reached the end!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You&apos;ve seen all {totalItems} articles. Check back later for more news.
            </Typography>
          </Box>
        </Grid>
      )}
    </>
  )
}
